import { clsx } from "clsx";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { config } from "../../state/config/config.selector";
import type { AppState } from "../../state/root.reducer";
import { userToken as selectUserToken } from "../../state/user/user.selector";
import {
  getWorkspaceThumbnailUrl,
  selectDashboardWorkspaceDetailsById,
  selectHasModifiedThumbnail,
} from "../../state/workspaces/workspaces.selector";
import { useI18n } from "../../utils/hooks/use.i18n";
import Styles from "./template.card.module.css";

type TemplateCardProps = {
  templateName: string;
  enabled: boolean;
  workspaceId?: string;
  onClick?: () => void;
  imageURL?: string;
  tooltip?: string;
  tooltipPostfix?: string;
  isDefaultModule?: boolean;
};

export const TemplateCard = ({
  enabled,
  imageURL,
  isDefaultModule = false,
  onClick,
  templateName,
  tooltip = "",
  tooltipPostfix = "",
  workspaceId,
}: TemplateCardProps) => {
  const t = useI18n("TEMPLATE_CARD.");
  const tcw = useI18n("CREATE_WORKSPACES_SECTION.");
  const additionalProps = isDefaultModule
    ? {
        tabIndex: 0,
        "aria-label": tcw("MODULES.BLANK_WORKSPACE"),
      }
    : {};

  const token = useSelector(selectUserToken);
  const conf = useSelector(config);
  const workspaceDetails = useSelector((state: AppState) =>
    selectDashboardWorkspaceDetailsById(state, workspaceId),
  );
  const ignoreCache = useSelector((state: AppState) =>
    selectHasModifiedThumbnail(state, workspaceId),
  );

  const [thumbnailUrl, setThumbnailUrl] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    const fetchThumbnail = async (workspaceId: string) => {
      let url = getWorkspaceThumbnailUrl(workspaceId, conf);
      if (ignoreCache) {
        url += "?defyCache=" + Date.now();
      }

      try {
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error("no thumbnail image");
        }
        const blob = await response.blob();
        setThumbnailUrl(URL.createObjectURL(blob));
      } catch {
        setThumbnailUrl(undefined);
      }
    };

    if (workspaceDetails?.thumbnailUrl?.startsWith("blob:")) {
      setThumbnailUrl(workspaceDetails.thumbnailUrl);
    } else if (workspaceId) {
      fetchThumbnail(workspaceId);
    }
  }, [conf, ignoreCache, token, workspaceDetails?.thumbnailUrl, workspaceId]);

  return (
    <div
      className={clsx(Styles.useCaseCard, !enabled && Styles.disabled)}
      data-test-id={`create-${templateName}`}
      onClick={onClick}
      title={tooltip + templateName + tooltipPostfix}
      {...additionalProps}
    >
      <div className={Styles.content}>
        {imageURL ? (
          <img
            alt={t("TILE_IMAGE")}
            className={Styles.moduleImage}
            src={thumbnailUrl ?? imageURL}
          />
        ) : (
          <div className={Styles.moduleStub}>
            <div
              className={clsx(Styles.moduleStubIcon, "hoylu-ui-icons-plus")}
            />
          </div>
        )}
      </div>
    </div>
  );
};
